import { FiX } from "react-icons/fi"
import Swal from "sweetalert2"
import { useFormik } from "formik"
import * as yup from "yup"
import useAuth from "../hooks/useAuth"
import useAxiosPrivate from "../hooks/useAxiosPrivate"
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material"
import { titleCase } from "../utils/helpers"

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "100%", sm: "50%" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  color: "#000000",
  border: "0px",
  boxShadow: 24,
  px: 2,
  py: 4,
  outline: "none",
  overflow: "auto",
}

const validationSchema = yup.object().shape({
  first_name: yup
    .string()
    .required("This field is required")
    .min(3, "First name must be at least 3 characters"),
  last_name: yup
    .string()
    .required("This field is required")
    .min(3, "Last name must be at least 3 characters"),
  email: yup
    .string()
    .email("Enter a valid email")
    .required("This field is required"),
})

const EditAdminModal = ({ openModal, setOpenModal, adminData }) => {
  const { auth } = useAuth()
  const axiosPrivate = useAxiosPrivate()
  const request = {
    headers: {
      authorization: `Bearer ${auth.accessToken}`,
    },
  }

  const formik = useFormik({
    initialValues: {
      first_name: adminData.first_name,
      last_name: adminData.last_name,
      email: adminData.email,
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async values => {
      const res = await axiosPrivate
        .put(
          `admins/companies/${adminData?.id}`,
          {
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email,
          },
          request
        )
        .catch(error => {
          Swal.fire({
            toast: true,
            icon: "error",
            title: "Oops...",
            text: error.response.data.message,
            position: "top",
            timer: 3000,
            showConfirmButton: false,
          })
        })
      if (res) {
        Swal.fire({
          toast: true,
          icon: "success",
          title: "Admin Updated",
          text: res.data.message,
          position: "top",
          timer: 3000,
          showConfirmButton: false,
        })
        setOpenModal(false)
      }
    },
  })

  return (
    <Modal
      open={openModal}
      aria-labelledby="modal-modal-title"
      disableScrollLock
    >
      <Box sx={style}>
        <Box sx={{ position: "relative" }}>
          <IconButton
            sx={{ position: "fixed", top: "0px", right: "0px" }}
            onClick={() => {
              setOpenModal(false)
            }}
          >
            <FiX />
          </IconButton>
        </Box>

        <Typography
          id="modal-modal-title"
          variant="h6"
          component="h2"
          sx={{ textAlign: "center", fontWeight: "700" }}
        >
          Edit Admin Details
        </Typography>

        <Box
          component="form"
          noValidate
          autoComplete="off"
          sx={{
            width: "80%",
            mt: "1rem",
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            "& .MuiTextField-root": { mt: "1rem" },
            "& .MuiSvgIcon-root": {
              color: "#000000",
            },
          }}
          onSubmit={formik.handleSubmit}
        >
          {/**first name */}
          <TextField
            required
            variant="outlined"
            size="small"
            fullWidth
            id="first_name"
            label="First Name"
            value={titleCase(formik.values.first_name)}
            onChange={formik.handleChange("first_name")}
            error={
              formik.touched.first_name && Boolean(formik.errors.first_name)
            }
            helperText={formik.touched.first_name && formik.errors.first_name}
          />

          {/**last name */}
          <TextField
            required
            variant="outlined"
            size="small"
            fullWidth
            id="last_name"
            label="Last Name"
            value={titleCase(formik.values.last_name)}
            onChange={formik.handleChange("last_name")}
            error={formik.touched.last_name && Boolean(formik.errors.last_name)}
            helperText={formik.touched.last_name && formik.errors.last_name}
          />

          {/**email */}
          <TextField
            required
            variant="outlined"
            size="small"
            fullWidth
            id="email"
            label="Email"
            value={formik.values.email}
            onChange={formik.handleChange("email")}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />

          <Button
            disabled={!formik.dirty}
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: "1rem" }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
export default EditAdminModal
